import { useMemo } from "react";
import { ServiceQuotesOfferingForm } from "@/components/proposal-formats/ServiceQuotesOfferingForm";
import { ghostBtnClass, primaryBtnClass } from "./serviceShared";
import type { useServiceOfferingsCatalog } from "./useServiceOfferingsCatalog";
import type { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import { ServiceDefinitionResourcesSection } from "./ServiceDefinitionResourcesSection";
import { ServiceOfferingDocumentsSection } from "./ServiceOfferingDocumentsSection";
import { ServiceSetupSalesSection } from "./ServiceSetupSalesSection";

const WIZARD_STEPS = [
  { id: 1, title: "Name your service" },
  { id: 2, title: "Assign crew & equipment" },
  { id: 3, title: "Upload documents" },
  { id: 4, title: "Make it quotable?" },
] as const;

export function ServiceSetupFirstServiceWizard(props: {
  step: number;
  offeringId: string | null;
  catalog: ReturnType<typeof useServiceOfferingsCatalog>;
  definitions: ReturnType<typeof useServiceDefinitionsPanel>;
  onStepChange: (step: number) => void;
  onOfferingCreated: (offeringId: string) => void;
  onFinish: () => void;
}) {
  const { step, offeringId, catalog, definitions, onStepChange, onOfferingCreated, onFinish } = props;
  const offering = useMemo(() => {
    if (!offeringId) return null;
    return (
      catalog.offerings.find((o) => o.id === offeringId) ??
      definitions.offerings.find((o) => o.id === offeringId) ??
      null
    );
  }, [catalog.offerings, definitions.offerings, offeringId]);

  async function handleStep1Save() {
    const saved = await catalog.saveOffering();
    if (saved) {
      onOfferingCreated(saved.id);
      definitions.openOfferingScope(saved.id);
      onStepChange(2);
    }
  }

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5 dark:border-indigo-900 dark:bg-indigo-950/20">
      <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">Set up your first service</h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
        Four quick steps — only the name is required; you can skip the rest and finish later.
      </p>

      <ol className="mt-4 flex flex-wrap gap-2">
        {WIZARD_STEPS.map((s) => (
          <li
            key={s.id}
            className={
              "rounded-full px-3 py-1 text-xs font-medium " +
              (step === s.id
                ? "bg-indigo-600 text-white"
                : step > s.id
                  ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-100"
                  : "bg-white text-gray-500 ring-1 ring-gray-200 dark:bg-zinc-900 dark:text-zinc-400 dark:ring-zinc-700")
            }
          >
            {s.id}. {s.title}
          </li>
        ))}
      </ol>

      {catalog.localError ? (
        <p className="mt-3 text-sm text-red-700 dark:text-red-200">{catalog.localError}</p>
      ) : null}

      {step === 1 ? (
        <div className="mt-4 space-y-3">
          <ServiceQuotesOfferingForm
            draft={catalog.draft}
            setDraft={catalog.setDraft}
            queues={catalog.queues}
            queuesUnavailable={catalog.queuesUnavailable}
            actionBusy={catalog.actionBusy}
            hideQuotable
            onSave={() => void handleStep1Save()}
          />
        </div>
      ) : null}

      {step >= 2 && offering ? (
        <div className="mt-4 space-y-4">
          {step === 2 ? (
            <ServiceDefinitionResourcesSection
              m={definitions}
              title="Crew & equipment"
              description="Add rigs and technicians for this service. Skip if you will assign them later."
            />
          ) : null}
          {step === 3 ? (
            <ServiceOfferingDocumentsSection
              offeringId={offering.id}
              offeringLabel={offering.label}
              presentation="card"
            />
          ) : null}
          {step === 4 ? <ServiceSetupSalesSection m={definitions} /> : null}

          <div className="flex flex-wrap gap-2">
            {step < 4 ? (
              <>
                <button type="button" className={ghostBtnClass} onClick={() => onStepChange(step + 1)}>
                  Skip
                </button>
                <button type="button" className={primaryBtnClass} onClick={() => onStepChange(step + 1)}>
                  Continue
                </button>
              </>
            ) : (
              <>
                <button type="button" className={ghostBtnClass} onClick={onFinish}>
                  Skip
                </button>
                <button type="button" className={primaryBtnClass} onClick={onFinish}>
                  Finish setup
                </button>
              </>
            )}
          </div>
        </div>
      ) : step > 1 && !offering ? (
        <p className="mt-4 text-sm text-amber-800 dark:text-amber-200">
          Finish step 1 to continue the wizard.
        </p>
      ) : null}
    </div>
  );
}
